import { Button } from 'antd';
import { Fragment, useCallback, useContext } from 'react';
import { useStore } from 'zustand';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { LegacyHotelContext } from 'app/context/HotelContext/LegacyHotelContext';
import { useExperiencesCategories } from 'app/hooks/data/useExperience';
import type { BulkEditInitialValues } from 'app/typings/bulkEdit';
import { ChannelManager } from 'app/typings/channelManagers';
import { RateMode } from 'app/typings/rateModes';
import { eachDayOfInterval } from 'app/utils/dayjs';
import { amplitudeTrack } from 'app/utils/track/amplitude';

import { BULK_EDIT_DEFAULT_DAYS } from '../BulkEdit/_components/DatesSelector';
import { inventoryStore } from '../InventoryStore';
import { RowTitleCell } from '../_components/Cells/_components/RowTitleCell';
import { InventoryCollapseTable } from '../_components/InventoryCollapseTable/InventoryCollapseTable';
import type { TimePeriod } from '../type';
import {
  computeCrossedPriceDescription,
  getTooltipDescription,
} from '../utils';

import { Package } from './Package';
import { InventoryRow } from './_components/InventoryRow';
import { getRoomIconParameters } from './utils';

type BulkEditButtonProps = {
  roomId?: number;
};

export const BulkEditButton = ({ roomId }: BulkEditButtonProps) => {
  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);

  const { hotelInfo: hotel } = useContext(HotelContext);

  const setIsBulkEditOpen = useStore(
    inventoryStore,
    (state) => state.setIsBulkEditOpen
  );
  const bulkEditInitialValues = useStore(
    inventoryStore,
    (state) => state.bulkEditInitialValues
  );

  const setBulkEditInitialValues = useStore(
    inventoryStore,
    (state) => state.setBulkEditInitialValues
  );

  const openBulkEdit = useCallback(
    (
      clickEvent: React.MouseEvent<HTMLElement, MouseEvent>,
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      bulkEditInitialValues: BulkEditInitialValues
    ) => {
      clickEvent.stopPropagation();
      amplitudeTrack('Open Bulk Edit');
      setIsBulkEditOpen(true);
      setBulkEditInitialValues(bulkEditInitialValues);
    },
    [setBulkEditInitialValues, setIsBulkEditOpen]
  );

  if (
    hotel?.channelManager !== ChannelManager.NONE &&
    hotel?.rateMode === RateMode.MULTI_RATE
  ) {
    return <></>;
  }

  return (
    <Button
      onClick={(clickEvent) =>
        !!roomId &&
        openBulkEdit(clickEvent, {
          ...bulkEditInitialValues,
          days: BULK_EDIT_DEFAULT_DAYS,
          roomId,
        })
      }
      disabled={isEditMode}
    >
      Bulk edit
    </Button>
  );
};

type RoomProps = {
  roomId: number;
  timePeriod: TimePeriod;
  shouldShowOverview: boolean;
};

export const Room = ({ roomId, timePeriod, shouldShowOverview }: RoomProps) => {
  const { currencyFormatter } = useContext(CurrencyContext);
  const { hotelInfo } = useContext(HotelContext);
  const { getPackagesForRoom, rooms, hasAtLeastOneDayPackage } =
    useContext(LegacyHotelContext);

  const hotel = hotelInfo;
  const { data: experienceCategories } = useExperiencesCategories();
  const hasChannelManager = hotel?.channelManager !== ChannelManager.NONE;
  const hasAutoInventoryActivated = hotel?.stockScrapperEnabled ?? false;

  const packages = getPackagesForRoom(roomId);

  const isEditMode = useStore(inventoryStore, (state) => state.isEditMode);
  const selectPackage = useStore(
    inventoryStore,
    (state) => state.selectPackage
  );

  const filteredPackages = packages.filter((p) => selectPackage.includes(p.id));

  if (filteredPackages.length === 0) {
    return null;
  }

  const allDates = eachDayOfInterval({
    start: timePeriod.startDate,
    end: timePeriod.endDate,
  });

  // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
  const room = rooms?.find((room) => room.id === roomId);

  const dayPackage = packages.filter((pkg) => pkg.dayPackage)[0];

  const dayPackageExperienceDescription = dayPackage
    ? computeCrossedPriceDescription(
        dayPackage.id,
        dayPackage.experiences,
        currencyFormatter,
        !!dayPackage,
        experienceCategories
      )
    : undefined;

  const renderPackages = () => {
    return filteredPackages.map((pkg) => (
      <Fragment key={pkg.id}>
        <Package packageId={pkg.id} roomId={roomId} timePeriod={timePeriod} />
      </Fragment>
    ));
  };

  return (
    <InventoryCollapseTable
      title={room?.name ?? room?.categoryName ?? ''}
      keyPanel={room?.id.toString() ?? ''}
      className="main-collapse collapse-container"
      extraHeader={<BulkEditButton roomId={room?.id} />}
      collapsible={shouldShowOverview}
    >
      <div className="table">
        {hasAutoInventoryActivated && (
          <InventoryRow
            dates={allDates}
            roomId={roomId}
            fieldName={'closed'}
            isEditable={isEditMode}
            firstColumnContent="Stop sell"
            getValue={(datePlanning) => datePlanning.stock}
          />
        )}
        <InventoryRow
          dates={allDates}
          roomId={roomId}
          fieldName={'stock'}
          isEditable={!hasChannelManager}
          firstColumnContent="Remaining stock"
          getValue={(datePlanning) => datePlanning.stock}
          getIconParameters={(datePlanning) =>
            getRoomIconParameters(
              hotelInfo,
              room,
              datePlanning,
              datePlanning.autoStock ||
                (datePlanning.bar === undefined && datePlanning.stock === 0)
            )
          }
        />
        <InventoryRow
          dates={allDates}
          roomId={roomId}
          fieldName={'booked'}
          firstColumnContent="Total booking"
          isEditable={false}
          getValue={(datePlanning) => datePlanning.booked}
        />
        <InventoryRow
          dates={allDates}
          roomId={roomId}
          fieldName={'bar'}
          firstColumnContent={
            <RowTitleCell
              value={
                hasAtLeastOneDayPackage
                  ? 'Public price /2 pers.'
                  : 'BAR RO (NA/NR)'
              }
              withTooltip
              tooltipTitle={getTooltipDescription(
                'bar',
                dayPackageExperienceDescription,
                undefined,
                hasAtLeastOneDayPackage
              )}
            />
          }
          isEditable={!hasChannelManager}
          getValue={(datePlanning) => currencyFormatter(datePlanning.bar || 0)}
          getIconParameters={(datePlanning) =>
            getRoomIconParameters(
              hotel,
              room,
              datePlanning,
              datePlanning.autoBar || datePlanning.bar === undefined
            )
          }
        />
      </div>
      {renderPackages()}
    </InventoryCollapseTable>
  );
};
